<template>
  <div class="screen intro">
    <div class="intro__stage">
      <img :src="burst" class="intro__burst" alt="" aria-hidden="true" />
      <div class="intro__hero">
        <!-- the badge is FIRST so it paints behind the star+drum, as in Figma -->
        <img :src="badge" class="intro__badge" alt="Elke week 'n spel!" />
        <img :src="starDrum" class="intro__star" alt="" aria-hidden="true" />
        <img :src="sign" class="intro__sign" alt="Het Priority Spel" />
      </div>
    </div>
    <div class="intro__body">
      <p class="intro__sub">Draai aan het rad, speel spellen en win toffe prijzen!</p>
      <div class="intro__cta">
        <AppButton variant="cta" :loading="starting" @click="start">Speel mee</AppButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '@/stores/game'
import AppButton from '@/components/ui/AppButton.vue'
import burst from '@/assets/v3/sunburst-bg.webp'
import starDrum from '@/assets/v3/intro-star-drum.webp'
import badge from '@/assets/v3/intro-badge.webp'
import sign from '@/assets/v3/priority-sign.webp'

const store = useGameStore()
const router = useRouter()
const starting = ref(false)

function start() {
  starting.value = true
  router.push({ name: store.seenGlobalIntro ? 'home' : 'uitleg' })
}
</script>

<style lang="scss" scoped>
// -----------------------------------------------------------------------------
// Layout: the star must sit exactly on the point the sunburst's rays radiate
// from, and that shared point must sit in the MIDDLE OF THE SPACE LEFT OVER
// above the tagline + CTA (not at a fixed fraction of the viewport). So the
// screen is two flex items:
//
//   .intro__stage  flex: 1 1 auto  -> exactly the leftover space; its own
//                                     vertical centre IS the anchor (top: 50%).
//   .intro__body   flex: 0 0 auto  -> the tagline + CTA, intrinsic height.
//
// Both artworks hang on that one anchor by their own convergence / centre
// point, so they can never drift apart:
//
//   --burst-x / --burst-y  where the rays converge inside sunburst-bg.webp,
//                          measured on the asset two independent ways (edge-line
//                          fit + radial colour constancy) which agree to ~2px.
//   --star-x  / --star-y   the circumcentre of the five tips of
//                          intro-star-drum.webp (measured; all five sit 163.7px
//                          from it). NOT the middle of the asset box - a
//                          five-pointed star hangs lower than its bounding box.
//
// Sizing: Figma frame "introscherm" 421:1040 is 390x771 and draws
// intro-star-drum.webp at x=0, y=58, 386x372 - i.e. the hero is the FULL width
// of the artboard (386/390 = 98.97%), not 336px as an earlier reading had it.
// Verified by rendering the frame and the asset and fitting the drum's panels:
// the two agree to the pixel on x, and to +58px on y. Everything inside the
// hero is therefore a plain % of the hero box, straight off that frame:
//
//   badge 421:1042  x 238..335, y  91..188  -> 61.66% / 8.87%, 25.13% wide
//   sign  421:1098  x  86..294, y 346..501  -> 22.28% / -19.09%, 53.89% wide
//
// The badge is BELOW the key visual in Figma's z-order (and in the DOM here),
// so it tucks in behind the drum.
// -----------------------------------------------------------------------------
.intro {
  --burst-x: 49.4%;
  --burst-y: 40.95%;
  --star-x: 49.85%;
  --star-y: 54.01%;

  // sunburst-bg.webp is 1392x2458 -> height = width / 0.566314
  --burst-aspect: 0.566314;

  // the mobile column: the screen, capped by #app's max-width
  --col: min(100vw, var(--app-max-width));

  // Figma draws intro-star-drum.webp 386 wide on the 390 artboard, so the hero
  // is 98.97% of the column. On a short screen it shrinks instead of crowding
  // the tagline: the hero hangs 0.6508 of its own HEIGHT below the anchor (the
  // star below its centre, plus the neon sign), the anchor is the middle of the
  // stage, and the stage is (viewport - 154px of copy - the 11dvh bottom pad),
  // so it fits while  hero width <= 0.7093 * viewport height - 155px.
  --hero-w: max(140px, min(calc(0.9897 * var(--col)), calc(0.7093 * 100svh - 155px)));

  position: relative;
  overflow: hidden;
  background: var(--c-red);
  color: var(--c-on-brand);
}

// The leftover space above the copy. Its centre is the anchor.
.intro__stage {
  position: relative;
  flex: 1 1 auto;
  // Floor for the hero at its smallest (min-height below): the anchor is the
  // centre, so the stage needs twice the deepest overhang plus breathing room.
  // On a normal phone flex wins and this floor is inert; on a very short
  // viewport the screen grows past 100dvh and scrolls instead of overlapping.
  min-height: 216px;
}

// Sized to behave like the old `background-size: cover`, but scaled up just
// enough that it still reaches both screen edges after being pulled up to the
// anchor. With the convergence point pinned at half the stage height (L) and
// 40.95% of the image above it, the image height H must satisfy:
//   top edge    <= 0      ->  H >= (L / 2) / 0.4095            = 0.6105 * L
//   bottom edge >= 100dvh ->  H >= (100dvh - L / 2) / 0.5905
//                                 = 1.6935 * 100dvh - 0.8467 * L
//   never narrower than the column (what `cover` guaranteed)
//                         ->  H >= column width / 0.566314     = 1.7658 * --col
// 100% here is the stage height (L), because the image is absolutely positioned
// inside the stage. The 1.5% is slack against sub-pixel rounding at the seam.
.intro__burst {
  position: absolute;
  left: var(--burst-x);
  top: 50%;
  width: auto;
  max-width: none;
  height: calc(1.015 * max(0.6105 * 100%, 1.6935 * 100dvh - 0.8467 * 100%, 1.7658 * var(--col)));
  // put the image's convergence point (not its centre) on the anchor
  transform: translate(calc(-1 * var(--burst-x)), calc(-1 * var(--burst-y)));
  pointer-events: none;
}

.intro__hero {
  position: absolute;
  left: var(--burst-x);
  top: 50%;
  width: var(--hero-w);
  height: calc(var(--hero-w) * 372 / 386);
  transform: translate(calc(-1 * var(--star-x)), calc(-1 * var(--star-y)));
}

.intro__star {
  display: block;
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 10px 24px rgba(0, 0, 0, 0.28));
}

.intro__badge {
  position: absolute;
  left: 61.66%; // Figma 421:1042 (97px at x=238 on a 390 artboard)
  top: 8.87%;
  width: 25.13%;
  height: auto;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.intro__sign {
  position: absolute;
  left: 22.28%; // Figma 421:1098 (208px at x=86 on a 390 artboard)
  bottom: -19.09%;
  width: 53.89%;
  height: auto;
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.35));
}

.intro__body {
  position: relative; // above the sunburst
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 var(--sp-6) clamp(var(--sp-6), 11dvh, 104px);
  text-align: center;
}

.intro__sub {
  max-width: 271px; // Figma 421:1085
  margin-bottom: var(--sp-8);
  font-size: 21px; // Figma 421:1085
  line-height: 27px;
  color: var(--c-white);
}

.intro__cta {
  width: min(291px, 100%);
}
</style>
